import { Box, Grid } from '@mui/material';
import { Fragment, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { createWorker } from '../../api/workers/createWorker';
import { removeWorker } from '../../api/workers/removeWorker';
import { updateWorker } from '../../api/workers/updateWorker';
import { Worker } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormButtonSelect from '../../components/forms/FomsButtonSelect';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import FormUploadImg from '../../components/FormUploadImg';
import {
  CredentialKey,
  adminCredentials,
  chefCredentials,
  credentialsMap,
  emptyCredentials,
  waiterCredentials,
} from '../../constants/credentials';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useUser } from '../../contexts/user.context';
import useMedia from '../../utils/useMedia';

type Props = {
  open: boolean;
  worker?: DbDocument<Worker> | null;
  onClose: () => void;
};

const EditWorker = ({ worker, open, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { lang } = useLocale();
  const {
    user: { phone, email },
  } = useUser();
  const { isMobile } = useMedia();
  const { workers } = useCafe();

  const isMyProfile = useMemo(
    () => worker && ((phone && phone === worker?.phone) || (email && email === worker?.email)),
    [email, phone, worker]
  );

  const form = useForm<Worker>({
    mode: 'onChange',
    defaultValues: {
      name: worker?.name || '',
      email: worker?.email || '',
      phone: worker?.phone || '',
      photoUrl: worker?.photoUrl || '',
      role: worker?.role || 'admin',
      credentials: worker?.id ? { ...emptyCredentials, ...(worker?.credentials || {}) } : adminCredentials,
      balance: worker?.balance || 0,
      ratePerHour: worker?.ratePerHour || 0,
      percentageOfSales: worker?.percentageOfSales || 0,
      pin: worker?.pin || undefined,
    },
  });

  const { setValue } = form;

  const credentials = form.watch('credentials');

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = async (newWorker: Worker) => {
    const payload: Worker = {
      ...newWorker,
      ratePerHour: Number(newWorker.ratePerHour),
      percentageOfSales: Number(newWorker.percentageOfSales),
      balance: Number(newWorker.balance),
    };

    if (worker?.id) {
      await updateWorker({ id: worker.id, newWorker: payload, cafeSlug });
    } else {
      await createWorker({ worker: payload, cafeSlug });
    }

    handleClose();
  };

  const handleRemoveWorker = async () => {
    if (worker?.id) {
      await removeWorker({ worker, cafeSlug });
      handleClose();
    }
  };

  const handleChangeRole = useCallback(
    (role: string | number) => {
      if (role === 'admin') {
        setValue('credentials', adminCredentials);
      }
      if (role === 'waiter') {
        setValue('credentials', waiterCredentials);
      }
      if (role === 'cook' || role === 'bartender') {
        setValue('credentials', chefCredentials);
      }
    },
    [setValue]
  );

  const validateDuplicatePhone = useCallback(
    (value: string) =>
      !value ||
      ((value === worker?.phone || workers.every((worker) => worker.phone !== value)) &&
        /^[+]{1}[0-9]{11,15}$/g.test(value)),
    [worker?.phone, workers]
  );

  const validateDuplicateEmail = useCallback(
    (value: string) => !value || value === worker?.email || workers.every((worker) => worker.email !== value),
    [worker?.email, workers]
  );

  return (
    <EditModal<Worker>
      title={t.worker[lang]}
      open={open}
      onClose={handleClose}
      submitButton={worker ? t.save_changes[lang] : t.add[lang]}
      onSubmit={saveChanges}
      secondaryButton={worker && !isMyProfile ? t.remove[lang] : undefined}
      confirmText={t.remove_worker_confirm[lang]}
      onSecondaryButtonClick={handleRemoveWorker}
      secondaryButtonProps={{ disabled: worker?.role === 'owner' }}
      form={form}
      size="md"
    >
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
        <Box width={isMobile ? undefined : 414}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              <FormUploadImg name="photoUrl" defaultValue={worker?.photoUrl || ''} folder="avatars" />
            </Grid>

            <Grid item xs={12} sm={12} justifyContent="center">
              <FormTextField name="name" label={t.userName[lang]} rules={{ required: true }} fullWidth />
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormTextField
                name="phone"
                label={t.phone[lang]}
                rules={{ validate: validateDuplicatePhone }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormTextField
                name="email"
                label={t.email[lang]}
                rules={{ pattern: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g, validate: validateDuplicateEmail }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormPriceField name="ratePerHour" label={t.rate_per_hour[lang]} fullWidth />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormTextField
                name="percentageOfSales"
                type="number"
                label={t.percentage_of_sales[lang]}
                rules={{ min: 0, max: 100, valueAsNumber: true }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormTextField
                name="pin"
                type="number"
                label={t.pin_code_for_multiuser_terminal[lang]}
                rules={{ minLength: 4, maxLength: 4, valueAsNumber: true }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        <Box width={isMobile ? undefined : 414}>
          <Box sx={{ mb: 1 }}>
            <FormButtonSelect
              name="role"
              label={t.role_and_permissions[lang]}
              options={['admin', 'waiter', 'cook', 'other_role']}
              disabled={worker?.role === 'owner'}
              onChange={handleChangeRole}
            />
          </Box>

          {Object.entries(credentialsMap).map(([key, children]) => {
            if (key === 'self_order_points' && cafe?.type !== 'selfOrderPoins') {
              return null;
            }

            return (
              <Box key={key} mb={1}>
                <FormSwitch
                  name={`credentials.${key}`}
                  label={t[key as keyof typeof t][lang]}
                  disabled={(key === 'workers' || key === 'allow_create_shift_on_login') && worker?.role === 'owner'}
                />

                {children !== null &&
                  Object.entries(children).map(([key2, children2]) => (
                    <Fragment key={key2}>
                      <FormSwitch
                        sx={{ ml: 2 }}
                        size="small"
                        name={`credentials.${key2}`}
                        label={t[key2 as keyof typeof t][lang]}
                        disabled={!credentials[key as CredentialKey]}
                      />

                      {children2 !== null &&
                        Object.keys(children2).map((key3) => (
                          <Fragment key={key3}>
                            <FormSwitch
                              sx={{ ml: 4 }}
                              size="small"
                              name={`credentials.${key3}`}
                              label={t[key3 as keyof typeof t][lang]}
                              disabled={!credentials[key as CredentialKey] || !credentials[key2 as CredentialKey]}
                            />
                          </Fragment>
                        ))}
                    </Fragment>
                  ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    </EditModal>
  );
};

export default EditWorker;
